import { AbiParameter } from 'viem';

import { EASAttestation } from '@/types';
import { useEasAttestation } from './useEASAttestation';

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
const VERIFIED_COUNTRY_SCHEMA_UIDS = {
  84532: '0xef54ae90f47a187acc050ce631c55584fd4273c0ca9456ab21750921c3a84028',
  8453: '0x1801901fabd0e6189356b4fb52bb0ab855276d84f7ec140839fbd1f6801ca065',
};

const VERIFIED_BUSINESS_COUNTRY_SCHEMA_UIDS = {
  84532: '0x9191f54e6990c536841a6a7043f9c6b7e890058e55975d4ba2ae8f7544fe20a4',
  8453: '0xf87445e61219642b989807bc418e5d5fa8e3adb49e230891055a997121f6c80b',
};

type VerifiedCountryReqeust = {
  address: `0x${string}`;
  chainId: number;
  verifiedCountries: string[];
};

type VerifiedCountryResponse = {
  attestation: EASAttestation | undefined;
  attestationCountry: string;
  verified: boolean;
  isLoading: boolean;
};

const checkVerified = (verifiedCountries: string[], country: string): boolean => {
  if (!verifiedCountries || verifiedCountries.length == 0 || !country) {
    return false;
  }
  return verifiedCountries.includes(country);
};

const constructReturnData = (
  attestation: EASAttestation | undefined,
  verifiedCountries: string[],
  isLoading: boolean,
) => {
  if (!attestation) {
    return {
      attestation: undefined,
      attestationCountry: '',
      verified: false,
      isLoading: isLoading,
    };
  }

  const country =
    attestation.decodedData?.length == 1 ? (attestation.decodedData[0] as string) : '';
  return {
    attestation: attestation,
    attestationCountry: country,
    verified: attestation.valid && checkVerified(verifiedCountries, country),
    isLoading: isLoading,
  };
};

export const useVerifiedCountry = ({
  address = ZERO_ADDRESS,
  chainId,
  verifiedCountries,
}: VerifiedCountryReqeust): VerifiedCountryResponse => {
  const schemaUidVC = VERIFIED_COUNTRY_SCHEMA_UIDS[
    chainId as keyof typeof VERIFIED_COUNTRY_SCHEMA_UIDS
  ] as `0x${string}`;
  const schemaUidVBC = VERIFIED_BUSINESS_COUNTRY_SCHEMA_UIDS[
    chainId as keyof typeof VERIFIED_BUSINESS_COUNTRY_SCHEMA_UIDS
  ] as `0x${string}`;

  const abiParams: AbiParameter[] = [
    {
      name: 'verifiedBusinessCountry',
      type: 'string',
    },
  ];

  const { attestation: attestationVC, isLoading: isLoadingVC } = useEasAttestation({
    address,
    schemaUid: schemaUidVC,
    abiParams: abiParams,
  });
  const { attestation: attestationVBC, isLoading: isLoadingVBC } = useEasAttestation({
    address,
    schemaUid: schemaUidVBC,
    abiParams: abiParams,
  });

  return constructReturnData(
    attestationVC?.valid ? attestationVC : attestationVBC,
    verifiedCountries,
    isLoadingVC || isLoadingVBC,
  );
};
